import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  useEffect(() => {
    // Errors from the SSO round trip arrive as ?error=...
    const ssoError = searchParams.get('error');
    if (ssoError) setError(ssoError);
    authService.getSSOStatus().then(setSsoEnabled);
  }, [searchParams]);

  useEffect(() => {
    // Check if redirected from password reset with success message
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async () => {
    setError('');
    setLoading(true);
    try {
      await authService.startSSO(searchParams.get('returnTo'));
    } catch (err) {
      setError(err.response?.data?.error || 'SSO is unavailable. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <header>
        <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
        <h1>Remote Inspection</h1>
      </header>

      <main className="main">
        <div className="card" style={{maxWidth: '450px'}}>
          <h2>Login</h2>
          <p style={{marginBottom: '24px', color: '#64748b'}}>
            Sign in to access the inspection console
          </p>

          <form onSubmit={handleSubmit}>
            <div className="section">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="section">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" style={{fontSize: '14px', color: '#2563eb', textDecoration: 'none'}}>
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {successMessage && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: '#d1fae5',
                border: '1px solid #10b981',
                borderRadius: '8px',
                color: '#065f46'
              }}>
                <strong>✅ Success:</strong> {successMessage}
              </div>
            )}

            {error && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#991b1b'
              }}>
                <strong>⚠️ Error:</strong> {error}
              </div>
            )}

            <div className="section">
              <button type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

          {ssoEnabled && (
            <div className="section">
              <button type="button" onClick={handleSSO} disabled={loading}>
                Sign in with SSO
              </button>
            </div>
          )}

          <p className="hint" style={{textAlign: 'center', marginTop: '20px'}}>
            Don't have an account? <Link to="/signup" style={{color: '#2563eb', textDecoration: 'none', fontWeight: '500'}}>Sign up</Link>
          </p>

          <p className="hint" style={{textAlign: 'center', marginTop: '12px', fontSize: '14px'}}>
            <Link to="/privacy-policy" style={{color: '#6b7280', textDecoration: 'none'}}>Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default Login;

